import React, { useEffect, useState } from "react";
import { useLocation, useHistory, Link } from "react-router-dom";
import SEO from "../../common/SEO";
import HeaderOne from "../../common/header/HeaderOne";
import HeaderTopBar from "../../common/header/HeaderTopBar";
import FooterOne from "../../common/footer/FooterOne";
import Copyright from "../../common/footer/Copyright";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Order = () => {
  const location = useLocation();
  const history = useHistory();

  const [formData, setFormData] = useState({
    service_name: "",
    package_type: "",
    amount: "",
    affiliator: "",
  });

  const [user, setUser] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);

  // Coupon states
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [discountedAmount, setDiscountedAmount] = useState(null);
  const [couponError, setCouponError] = useState("");

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const service_name = queryParams.get("service");
    const package_type = queryParams.get("package");
    const amount = queryParams.get("amount");

    if (queryParams.get("affiliator")) {
      const id = queryParams.get("affiliator");
      const expires = Date.now() + 30 * 24 * 60 * 60 * 1000;
      localStorage.setItem("affiliate", JSON.stringify({ id, expires }));
    }

    let affiliator = queryParams.get("affiliator");
    if (!affiliator) {
      const storedAffiliate = localStorage.getItem("affiliate");
      if (storedAffiliate) {
        const parsed = JSON.parse(storedAffiliate);
        if (Date.now() < parsed.expires) {
          affiliator = parsed.id;
        } else {
          localStorage.removeItem("affiliate");
        }
      }
    }

    const userData = JSON.parse(localStorage.getItem("user"));

    if (!localStorage.getItem("token")) {
      history.push("/login");
      return;
    }

    setFormData({
      service_name,
      package_type,
      affiliator: affiliator || "",
      amount,
    });

    setDiscountedAmount(amount);
    setUser(userData || {});
  }, [location, history]);

  // ✅ Apply Coupon
  const applyCoupon = async () => {
    if (!couponCode.trim()) return;

    try {
      setCouponError("");
      const res = await fetch(`${API_BASE_URL}/validate-coupon`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ coupon_code: couponCode }), // ✅ fixed payload
      });

      const data = await res.json();

      if (!data.success) {
        setCoupon(null);
        setCouponError(data.message || "Invalid coupon.");
        setDiscountedAmount(formData.amount);
        return;
      }

      setCoupon(data.data);

      let finalAmount = Number(formData.amount);

      if (data.data.discount_type === "percentage") {
        finalAmount -= (finalAmount * data.data.discount_amount) / 100;
      } else if (data.data.discount_type === "fixed") {
        finalAmount -= Number(data.data.discount_amount);
      }

      if (finalAmount < 0) finalAmount = 0;

      setDiscountedAmount(finalAmount);
    } catch (err) {
      console.error("❌ Coupon error:", err);
      setCouponError("Failed to apply coupon.");
    }
  };

  // ✅ Handle Payment
  const handlePayment = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const finalAmount = Number(discountedAmount || formData.amount); 

      const res = await fetch(`${API_BASE_URL}/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          service_name: formData.service_name,
          package_type: formData.package_type,
          amount: discountedAmount || formData.amount,
          affiliator_id: formData.affiliator,
          coupon_code: couponCode || null,
        }),
      });

      const data = await res.json();
      console.log("✅ SSLCommerz response:", data);

      if (Number(discountedAmount || formData.amount) > 300000) {
        alert("✅ Your order has been placed successfully. Our team will contact you for payment.");
        history.push("/order-success");
      } else {
        if (data.payment_url) {
          window.location.href = data.payment_url;
        } else {
          if (finalAmount === 0.00) {
            history.push("/order-success");
          } else {
            setError("Payment URL not found. Please try again.");
          }
        }
      }
    } catch (err) {
      console.error("❌ Payment error:", err);
      setError("Payment initiation failed.");
    }

    setLoading(false);
  };

  return (
    <>
      <SEO title="Order" />
      <main className="page-wrapper">
        <HeaderTopBar />
        <HeaderOne btnStyle="btn-small" HeaderSTyle="header-transparent" />

        <div className="container py-5 rn-section-gap">
          <div className="mx-auto" style={{ maxWidth: "720px" }}>
            <h3 className="mb-5 text-center" style={{ fontWeight: "700" }}>
              Confirm Your Order
            </h3>

            <div
              className="card"
              style={{
                border: "1px solid violet",
                backgroundColor: "rgba(238, 130, 238, 0.1)",
              }}
            >
              <div className="card-body p-4">
                {/* Customer Info */}
                <h5 className="mb-4" style={{ fontWeight: "600" }}>
                  Customer Information
                </h5>
                <hr style={{ width: "200px", border: "1px solid violet" }} />
                <div className="row mb-4">
                  <div className="col-6 mb-3"><h5>Name:</h5><p>{user.name || "N/A"}</p></div>
                  <div className="col-6 mb-3"><h5>Email:</h5><p>{user.email || "N/A"}</p></div>
                  <div className="col-6 mb-3"><h5>Phone:</h5><p>{user.phone_number || "N/A"}</p></div>
                  <div className="col-6 mb-3"><h5>Address:</h5><p>{user.address || "N/A"}</p></div>
                </div>

                {/* Service Info */}
                <h5 className="mb-4" style={{ fontWeight: "600" }}>Service Details</h5>
                <hr style={{ width: "200px", border: "1px solid violet" }} />
                <div className="row mb-4">
                  <div className="col-6 mb-3"><h5>Service: <span>{formData.service_name || "N/A"}</span></h5></div>
                  <div className="col-6 mb-3"><h5>Package: <span>{formData.package_type || "N/A"}</span></h5></div>
                  <div className="col-6 mb-3">
                    <h5>
                      Amount:{" "}
                      <span className={coupon ? "fw-light text-decoration-line-through text-danger" : "fw-light"}>
                        ৳{formData.amount || "0"}
                      </span>
                    </h5>
                  </div>
                  <div className="col-6 mb-3">
                    <h5>Payable: <span className="fw-bold text-success">৳{discountedAmount || formData.amount}</span></h5>
                  </div>
                  {formData.affiliator && (
                    <div className="col-6 mb-3"><h5>Affiliate: <span>{formData.affiliator}</span></h5></div>
                  )}
                </div>

                {/* Coupon Section */}
                <div className="mb-4">
                  <label className="form-label fw-bold text-primary">Have a coupon?</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control no-focus-style focus:bg-transparent"
                      style={{ border: "1px solid violet", backgroundColor: "transparent"}}
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                    />
                    <button className="btn-default" type="button" onClick={applyCoupon}>
                      Apply
                    </button>
                  </div>
                  {couponError && <p className="text-danger mt-2">{couponError}</p>}
                  {coupon && (
                    <p className="text-success mt-2">
                      ✅ Coupon applied: {coupon.discount_amount}
                      {coupon.discount_type === "percentage" ? "%" : "৳"} off
                    </p>
                  )}
                </div>

                {/* Policies */}
                <div className="form-check mb-4 text-center">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="acceptPolicies"
                    checked={acceptedPolicies}
                    onChange={(e) => setAcceptedPolicies(e.target.checked)}
                  />
                  <label className="form-check-label text-white" htmlFor="acceptPolicies">
                    I agree to the <Link to="/terms-and-condition">Terms & Conditions</Link>,{" "}
                    <Link to="/return-and-refund-policy">Return & Refund Policy</Link>, and{" "}
                    <Link to="/privacy-policy">Privacy Policy</Link>
                  </label>
                </div>

                {/* Errors */}
                {error && <div className="alert alert-danger text-center">{error}</div>}

                {/* Pay Button */}
                <div className="text-center">
                  <button
                    className="btn-default"
                    onClick={handlePayment}
                    disabled={loading || !acceptedPolicies}
                    style={{ borderRadius: "50px", fontWeight: "600" }}
                  >
                    {loading ? "Processing..." : "Pay Now"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <FooterOne />
        <Copyright />
      </main>
    </>
  );
};

export default Order;
